import { useEffect, useRef, useState } from "react";
import {
    Box,
    Button,
    Container,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { supabase } from "../../client/supabaseClient";
import { useAuth } from "../../client/AuthProvider";
import { useEditMode } from "../../context/EditModeContext";
import { useSupabaseTable } from "../../hooks/useSupabaseTable";

import TimelineControls from "./TimelineControls";
import TimelineList from "./TimelineList";
import EditDialog from "../../components/EditDialog";
import ConfirmDeleteDialog from "./ConfirmDeleteDialog";
import ImportJsonDialog from "./ImportJsonDialog";
import journeyData from "../../data/journey.json";

export type JourneyItem = {
    year: string;
    type?: string;
    role: string;
    company: string;
    points: string[];
};

type TimelineProps = {
    initialJson?: JourneyItem[];
};

export default function Timeline({ initialJson }: TimelineProps) {
    const CHUNK = 8;
    const { isEditMode } = useEditMode();
    const { user } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [editing, setEditing] = useState<JourneyItem | null>(null);
    const [isNew, setIsNew] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<JourneyItem | null>(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
    const [visibleCount, setVisibleCount] = useState<number>(CHUNK);
    const sentinelRef = useRef<HTMLDivElement | null>(null);

    const { data: supabaseItems, insert, update } = useSupabaseTable<JourneyItem>(
        "journey",
        { realtime: true }
    );

    const [items, setItems] = useState<JourneyItem[]>([]);

    useEffect(() => {
        if (supabaseItems) setItems(supabaseItems);
    }, [supabaseItems]);

    const visibleItems = items.slice(0, visibleCount);

    const handleAdd = () => {
        setEditing({ year: "", type: "", role: "", company: "", points: [] });
        setIsNew(true);
    };

    const handleSave = async (updated: JourneyItem) => {
        try {
            if (isNew) await insert(updated);
            else await update(updated, { year: editing?.year, role: editing?.role });
            setEditing(null);
            setIsNew(false);
        } catch (err) {
            console.error("Error saving journey item:", err);
        }
    };

    const importJson = async (jsonData: JourneyItem[]) => {
        try {
            for (const item of jsonData) await insert(item);
        } catch (err) {
            console.error("Error importing JSON:", err);
        }
    };

    const handleDeleteClick = (item: JourneyItem) => {
        setDeleteTarget(item);
        setConfirmOpen(true);
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        try {
            const { error } = await supabase
                .from("journey")
                .delete()
                .eq("year", deleteTarget.year)
                .eq("role", deleteTarget.role);

            if (!error) {
                setItems((prev) =>
                    prev.filter(
                        (i) => !(i.year === deleteTarget.year && i.role === deleteTarget.role)
                    )
                );
            }
            setDeleteTarget(null);
            setConfirmOpen(false);
        } catch (err) {
            console.error(err);
        }
    };

    const cancelDelete = () => {
        setDeleteTarget(null);
        setConfirmOpen(false);
    };

    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel) return;
        const io = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting)
                    setVisibleCount((prev) => Math.min(prev + CHUNK, items.length));
            },
            { root: null, rootMargin: "400px", threshold: 0.1 }
        );
        io.observe(sentinel);
        return () => io.disconnect();
    }, [items]);

    useEffect(() => {
        if (initialJson?.length) importJson(initialJson);
    }, [initialJson]);

    return (
        <Container sx={{ py: 4 }}>
            <Box>
                {isEditMode && (
                    <TimelineControls
                        onAdd={handleAdd}
                        onOpenJsonDialog={() => setJsonDialogOpen(true)}
                        disabled={!user}
                    />
                )}

                <TimelineList
                    items={visibleItems}
                    isEditMode={isEditMode}
                    isMobile={isMobile}
                    onEdit={setEditing}
                    onDelete={handleDeleteClick}
                />

                <div ref={sentinelRef} style={{ height: 1 }} />

                {visibleCount < items.length && (
                    <Box textAlign="center" mt={2}>
                        <Button
                            variant="outlined"
                            onClick={() =>
                                setVisibleCount((prev) => Math.min(prev + CHUNK, items.length))
                            }
                        >
                            Load more
                        </Button>
                    </Box>
                )}

                <EditDialog
                    open={!!editing}
                    item={editing}
                    onSave={handleSave}
                    onClose={() => setEditing(null)}
                />
            </Box>

            {/* Import JSON Dialog */}
            <ImportJsonDialog
                open={jsonDialogOpen}
                onClose={() => setJsonDialogOpen(false)}
                onSubmit={importJson}
                exampleJson={JSON.stringify(journeyData, null, 2)}
            />

            {/* Confirm Delete Dialog */}
            <ConfirmDeleteDialog<JourneyItem>
                open={confirmOpen}
                onCancel={cancelDelete}
                onConfirm={confirmDelete}
                target={deleteTarget}
                getDisplayText={(item) => `${item.company} - ${item.role} (${item.year})`}
            />

        </Container>
    );
}
